import { HERO_FETCH_PRIORITY } from "@/lib/heroImage";
import { Photo } from "@/components/Photo";
import { cn } from "@/lib/cn";
import { ButtonLink } from "@/components/ui/Button";
import type { ResponsiveImage } from "@/lib/responsiveImage";

export function PageHero({
  image,
  imageAlt,
  imagePosition,
  title,
  body,
  align = "center",
  cta,
  caption,
}: {
  image: ResponsiveImage;
  imageAlt: string;
  imagePosition?: string;
  title: string;
  body: string;
  align?: "center" | "start";
  cta?: { label: string; href: string };
  caption?: string;
}) {
  const start = align === "start";
  return (
    <div className="px-2 pt-2 sm:px-4 sm:pt-4">
      <section
        data-tone="dark"
        id="top"
        className="relative min-h-[calc(100svh-1rem)] overflow-hidden rounded-3xl pb-20 sm:min-h-[max(40rem,75vw)] sm:pb-0 lg:h-[calc(100svh-2rem)] lg:max-h-[67.5rem] lg:min-h-[35rem] bg-inverse text-inverse"
      >
        <Photo
          loading="eager"
          {...HERO_FETCH_PRIORITY}
          {...image}
          sizes="(min-width: 1024px) max(100vw, min(180vh, 1944px)), (min-width: 640px) 137vw, 182vh"
          alt={imageAlt}
          style={{ objectPosition: imagePosition }}
          className="absolute inset-0"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/50 to-transparent to-70%"
        />
        <div
          className={cn(
            "relative mx-auto flex max-w-6xl flex-col gap-6 px-8 pt-60 sm:px-16 sm:pt-48 lg:pt-64 xl:px-6",
            start
              ? "md:gap-10 lg:flex-row lg:items-end lg:justify-between lg:gap-6"
              : "items-center text-center",
          )}
        >
          <div
            className={cn(
              "flex max-w-3xl flex-col gap-6",
              start ? "flex-1" : "items-center",
            )}
          >
            <h1 className="text-balance text-4xl leading-tight sm:text-5xl lg:text-6xl">
              {title}
            </h1>
            <p
              className={cn(
                "text-base tracking-body md:text-lg",
                start ? "leading-title" : "max-w-md leading-normal",
              )}
            >
              {body}
            </p>
          </div>
          {cta && (
            <ButtonLink
              href={cta.href}
              size="lg"
              className="self-start lg:self-auto"
            >
              {cta.label}
            </ButtonLink>
          )}
        </div>
        {caption && (
          <p className="absolute bottom-4 right-4 rounded-full bg-white/25 px-4 py-2 text-xs uppercase leading-relaxed tracking-label backdrop-blur-sm">
            {caption}
          </p>
        )}
      </section>
    </div>
  );
}
